import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUserUrl } from '../utils/utils';

// Types (for documentation and clarity)
/**
 * @typedef {Object} CommonEventContext - Context for immutable event configuration. These are things like eventHostId, eventHostCompanyId etc which stay the same for all events of the host.
 * @property {string} eventId - The ID of the event
 * @property {string} hostId - The ID of the host
 * @property {string} hostCompanyId - The ID of the host company
 */
export const CommonEventContext = createContext(null);

export const CommonEventContextProvider = ({ children, value }) => {
  return (
    <CommonEventContext.Provider value={value}>
      {children}
    </CommonEventContext.Provider>
  );
};

/**
 * @typedef {Object} EventOperationsContext - Context for mutable event data and operations. These are things like agendas, tasks, assignment groups, addons etc.
 * @property {Array} agendas - Event agendas
 * @property {Array} tasks - Event tasks
 * @property {Array} assignmentGroups - Team assignment groups
 * @property {Array} addons - Event addons configuration
 */
export const EventOperationsContext = createContext(null);

export const EventOperationsContextProvider = ({ children, eventId }) => {
  const [agendas, setAgendas] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [addons, setAddons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchAgendas(),
          fetchTasks(),
          fetchAssignmentGroups(),
          fetchAddons()
        ]);
      } catch (error) {
        console.error('Error fetching event data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (eventId) {
      fetchAllData();
    }
  }, [eventId]);

  // Fetch functions
  const fetchAgendas = async () => {
    try {
      const response = await axios.get(`${getBackendUserUrl()}/agenda/agenda_items/${eventId}`);
      setAgendas(response.data || []);
    } catch (error) {
      console.error('Error fetching agendas:', error);
      setAgendas([]);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${getBackendUserUrl()}/task/tasks/${eventId}?include_assignees=true`);
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
    }
  };

  const fetchAssignmentGroups = async () => {
    try {
      const response = await axios.get(`${getBackendUserUrl()}/agenda/assignment_groups/${eventId}`);
      setAssignmentGroups(response.data);
    } catch (error) {
      console.error('Error fetching assignment groups:', error);
    }
  };

  const fetchAddons = async () => {
    try {
      const response = await axios.get(`${getBackendUserUrl()}/event/addons/${eventId}`);
      setAddons(response.data || []);
      console.log("fetchAddons: addons", response.data)
    } catch (error) {
      console.error('Error fetching addons:', error);
      setAddons([]);
    }
  };

  // Agenda operations
  const addAgenda = async (agendaData) => {
    try {
      const response = await axios.post(`${getBackendUserUrl()}/agenda/agenda_items/`, agendaData);
      await fetchAgendas();
      return response.data;
    } catch (error) {
      console.error('Error adding agenda:', error);
      throw error;
    }
  };

  const updateAgenda = async (agendaId, agendaData) => {
    try {
      const response = await axios.patch(`${getBackendUserUrl()}/agenda/agenda_items/${agendaId}`, agendaData);
      await fetchAgendas();
      return response.data;
    } catch (error) {
      console.error('Error updating agenda:', error);
      throw error;
    }
  };

  const deleteAgenda = async (agendaId) => {
    try {
      const response = await axios.delete(`${getBackendUserUrl()}/agenda/agenda_items/${agendaId}`);
      await fetchAgendas();
      return response.data;
    } catch (error) {
      console.error('Error deleting agenda:', error);
      throw error;
    }
  };

  // Assignment group operations
  const addAssignmentGroup = async (groupData) => {
    try {
      const response = await axios.post(`${getBackendUserUrl()}/agenda/assignment_groups/`, groupData);
      await fetchAssignmentGroups();
      return response.data;
    } catch (error) {
      console.error('Error adding assignment group:', error);
      throw error;
    }
  };

  const deleteAssignmentGroup = async (groupId) => {
    try {
      await axios.delete(`${getBackendUserUrl()}/agenda/assignment_groups/${groupId}`);
      await fetchAssignmentGroups();
    } catch (error) {
      console.error('Error deleting assignment group:', error);
      throw error;
    }
  };

  // Addon operations
  const addAddon = async (addonData) => {
    try {
      const response = await axios.post(`${getBackendUserUrl()}/event/${eventId}/addons`, addonData);
      await fetchAddons();
      return response.data;
    } catch (error) {
      console.error('Error adding addon:', error);
      throw error;
    }
  };

  const updateAddon = async (addonId, addonData) => {
    try {
      const response = await axios.patch(`${getBackendUserUrl()}/event/${eventId}/addons/${addonId}`, addonData);
      await fetchAddons();
      return response.data;
    } catch (error) {
      console.error('Error updating addon:', error);
      throw error;
    }
  };

  const deleteAddon = async (addonId) => {
    try {
      const response = await axios.delete(`${getBackendUserUrl()}/event/${eventId}/addons/${addonId}`);
      await fetchAddons();
      return response.data;
    } catch (error) {
      console.error('Error deleting addon:', error);
      throw error;
    }
  };

  // Task operations
  const addTask = async (taskData) => {
    try {
      const response = await axios.post(`${getBackendUserUrl()}/task/tasks/`, {
        event_id: eventId,
        agenda_id: taskData.agenda_id,
        name: taskData.name,
        description: taskData.description,
        priority: taskData.priority,
        status: taskData.status,
        created_by: taskData.created_by
      });
      await fetchTasks();
      return response.data;
    } catch (error) {
      console.error('Error adding task:', error);
      throw error;
    }
  };

  const updateTask = async (taskId, taskData) => {
    try {
      const response = await axios.patch(`${getBackendUserUrl()}/task/tasks/${taskId}`, {
        name: taskData.name,
        description: taskData.description,
        priority: taskData.priority,
        status: taskData.status,
        agenda_id: taskData.agenda_id
      });
      await fetchTasks();
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  const deleteTask = async (taskId, userId) => {
    try {
      await axios.delete(`${getBackendUserUrl()}/task/tasks/${taskId}?user_id=${userId}`);
      await fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  };

  // Task assignment operations
  const assignUsersToTask = async (taskId, userIds) => {
    try {
      const response = await axios.post(`${getBackendUserUrl()}/task/tasks/${taskId}/assign`, {
        user_ids: userIds
      });
      await fetchTasks(); // Refresh to get updated assignees
      return response.data;
    } catch (error) {
      console.error('Error assigning users to task:', error);
      throw error;
    }
  };

  const unassignUserFromTask = async (taskId, userId) => {
    try {
      const response = await axios.delete(`${getBackendUserUrl()}/task/tasks/${taskId}/unassign/${userId}`);
      await fetchTasks(); // Refresh to get updated assignees
      return response.data;
    } catch (error) {
      console.error('Error unassigning user from task:', error);
      throw error;
    }
  };

  const value = {
    // Data
    agendas,
    tasks,
    assignmentGroups,
    addons,
    isLoading,

    // Operations
    fetchAgendas,
    fetchTasks,
    fetchAssignmentGroups,
    fetchAddons,
    addAgenda,
    updateAgenda,
    deleteAgenda,
    addAssignmentGroup,
    deleteAssignmentGroup,
    addAddon,
    updateAddon,
    deleteAddon,
    addTask,
    updateTask,
    deleteTask,
    assignUsersToTask,
    unassignUserFromTask
  };

  return (
    <EventOperationsContext.Provider value={value}>
      {children}
    </EventOperationsContext.Provider>
  );
};

// Hooks
export const useCommonEventData = () => {
  const context = useContext(CommonEventContext);
  if (!context) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
};

export const useEventOperationsData = () => {
  const context = useContext(EventOperationsContext);
  if (!context) {
    throw new Error('useEventData must be used within an EventDataProvider');
  }
  return context;
}; 